// © 2024–2026 Jos Jozef Sytze van der Werf — Alle rechten voorbehouden.
// ─── Enums ────────────────────────────────────────────────────────────────────

export enum TrustedPersonRole {
  PRIMARY = 'PRIMARY',
  RESERVE = 'RESERVE',
}

export enum TrustedPersonRelationship {
  PARTNER  = 'PARTNER',
  CHILD    = 'CHILD',
  SIBLING  = 'SIBLING',
  FRIEND   = 'FRIEND',
  OTHER    = 'OTHER',
}

export enum ConfirmationStatus {
  PENDING   = 'PENDING',
  CONFIRMED = 'CONFIRMED',
  REVOKED   = 'REVOKED',
}

export enum ActivationTrigger {
  ON_DEATH      = 'ON_DEATH',
  ON_INCAPACITY = 'ON_INCAPACITY',
  IMMEDIATE     = 'IMMEDIATE',
  MANUAL        = 'MANUAL',
}

export enum ActivationStatus {
  INACTIVE = 'INACTIVE',
  PENDING  = 'PENDING',
  ACTIVE   = 'ACTIVE',
  REVOKED  = 'REVOKED',
  EXPIRED  = 'EXPIRED',
}

export enum ModulePermission {
  ALWAYS  = 'ALWAYS',
  VISIBLE = 'VISIBLE',
  HIDDEN  = 'HIDDEN',
  NEVER   = 'NEVER',
}

export enum SessionRole {
  TRUSTED_PERSON = 'TRUSTED_PERSON',
  NEXT_OF_KIN    = 'NEXT_OF_KIN',
}

export enum SessionStatus {
  ACTIVE  = 'ACTIVE',
  EXPIRED = 'EXPIRED',
  REVOKED = 'REVOKED',
}

export enum ActorRole {
  OWNER          = 'OWNER',
  TRUSTED_PERSON = 'TRUSTED_PERSON',
  RESERVE_PERSON = 'RESERVE_PERSON',
  NEXT_OF_KIN    = 'NEXT_OF_KIN',
  SYSTEM         = 'SYSTEM',
}

export enum LogEventType {
  AUTH_SUCCESS             = 'AUTH_SUCCESS',
  AUTH_FAILED              = 'AUTH_FAILED',
  AUTH_BLOCKED             = 'AUTH_BLOCKED',
  SESSION_STARTED          = 'SESSION_STARTED',
  SESSION_EXPIRED          = 'SESSION_EXPIRED',
  SESSION_REVOKED          = 'SESSION_REVOKED',
  MODULE_OPENED            = 'MODULE_OPENED',
  MESSAGE_READ             = 'MESSAGE_READ',
  KEY_GENERATED            = 'KEY_GENERATED',
  KEY_ROTATED              = 'KEY_ROTATED',
  KEY_REVOKED              = 'KEY_REVOKED',
  PERMISSIONS_CHANGED      = 'PERMISSIONS_CHANGED',
  EMERGENCY_REVOKE         = 'EMERGENCY_REVOKE',
  CORRECTION_WINDOW_OPENED = 'CORRECTION_WINDOW_OPENED',
  CORRECTION_WINDOW_CLOSED = 'CORRECTION_WINDOW_CLOSED',
  VERIFICATION_REQUESTED   = 'VERIFICATION_REQUESTED',
}

export enum LogOutcome {
  SUCCESS = 'SUCCESS',
  FAILED  = 'FAILED',
  BLOCKED = 'BLOCKED',
}

export enum DeviceType {
  DESKTOP = 'DESKTOP',
  MOBILE  = 'MOBILE',
  TABLET  = 'TABLET',
  UNKNOWN = 'UNKNOWN',
}

export enum MessageDraftStatus {
  DRAFT    = 'DRAFT',
  READY    = 'READY',
  RELEASED = 'RELEASED',
}

export enum SyncStatus {
  LOCAL_ONLY = 'LOCAL_ONLY',
  PENDING    = 'PENDING',
  SYNCED     = 'SYNCED',
  CONFLICT   = 'CONFLICT',
}

export enum WishCategory {
  PERSONAL  = 'PERSONAL',
  CARE      = 'CARE',
  HOUSING   = 'HOUSING',
  OTHER     = 'OTHER',
}

export enum CompletionStatus {
  NOT_STARTED  = 'NOT_STARTED',
  IN_PROGRESS  = 'IN_PROGRESS',
  COMPLETE     = 'COMPLETE',
}

export enum DocumentCategory {
  IDENTITY    = 'IDENTITY',
  FINANCIAL   = 'FINANCIAL',
  INSURANCE   = 'INSURANCE',
  PROPERTY    = 'PROPERTY',
  LEGAL       = 'LEGAL',
  MEDICAL     = 'MEDICAL',
  OTHER       = 'OTHER',
}

export enum DigitalServiceCategory {
  SOCIAL_MEDIA = 'SOCIAL_MEDIA',
  EMAIL        = 'EMAIL',
  BANKING      = 'BANKING',
  SUBSCRIPTION = 'SUBSCRIPTION',
  PHOTOS       = 'PHOTOS',
  OTHER        = 'OTHER',
}

export enum DigitalDesiredAction {
  DELETE      = 'DELETE',
  MEMORIALIZE = 'MEMORIALIZE',
  TRANSFER    = 'TRANSFER',
  OTHER       = 'OTHER',
}

export enum SignalType {
  SUGGESTION        = 'SUGGESTION',
  WARNING           = 'WARNING',
  REMINDER          = 'REMINDER',
  COMPLETION_PROMPT = 'COMPLETION_PROMPT',
  SAFETY            = 'SAFETY',
}

export enum SignalPriority {
  LOW    = 'LOW',
  MEDIUM = 'MEDIUM',
  HIGH   = 'HIGH',
}

export enum SignalStatus {
  UNREAD    = 'UNREAD',
  READ      = 'READ',
  DISMISSED = 'DISMISSED',
  ACTED_ON  = 'ACTED_ON',
}

export enum EventCategory {
  PROFILE  = 'PROFILE',
  WISHES   = 'WISHES',
  KEY      = 'KEY',
  ACCESS   = 'ACCESS',
  SETTINGS = 'SETTINGS',
  SYSTEM   = 'SYSTEM',
}

export enum ActorType {
  OWNER          = 'OWNER',
  SYSTEM         = 'SYSTEM',
  TRUSTED_PERSON = 'TRUSTED_PERSON',
}

// ─── Core entity interfaces ────────────────────────────────────────────────────

export interface UserProfile {
  id:               string
  createdAt:        string
  updatedAt:        string
  firstName:        string
  encryptedProfile: ArrayBuffer
  blobIv:           string
  blobVersion:      number
  cloudSyncEnabled: boolean
  cloudProfileId:   string | null
  lastLocalSave:    string
  schemaVersion:    number
  syncStatus:       SyncStatus
}

export interface DecryptedProfile {
  lastName:             string
  dateOfBirth:          string
  email:                string
  phone:                string
  address:              string   // legacy - volledig adres als string
  // V35-P2: separate adresvelden voor directe prefill
  street?:              string
  houseNumber?:         string
  houseNumberAddition?: string
}

export interface TrustedPerson {
  id:                  string
  userId:              string
  role:                TrustedPersonRole
  firstName:           string
  relationship:        TrustedPersonRelationship
  accessKeyId:         string
  confirmationStatus:  ConfirmationStatus
  confirmedAt:         string | null
  lastConfirmedAt?:    string          // V41: optioneel - geen schema-upgrade, geen index
  createdAt:           string
  updatedAt:           string
  revokedAt:           string | null
  syncStatus:          SyncStatus
  cloudSyncId:         string | null
  // Encrypted in blob:
  encryptedData:       ArrayBuffer | null
  blobIv:              string | null
}

export interface AccessKey {
  id:                  string
  userId:              string
  trustedPersonId:     string
  keyType:             TrustedPersonRole
  codeHash:            string
  dobHash:             string
  activationTrigger:   ActivationTrigger
  activationStatus:    ActivationStatus
  accessStartDate:     string | null
  accessEndDate:       string | null
  accessDurationDays:  number | null
  inactivityEnabled:   boolean
  inactivityDays:      number | null
  inactivityStartedAt: string | null
  generatedAt:         string
  lastRotatedAt:       string | null
  rotationCount:       number
  invalidatedAt:       string | null
  cloudSynced:         boolean
  syncStatus:          SyncStatus
}

export interface KeyPermissions {
  id:                     string
  accessKeyId:            string
  userId:                 string
  moduleEmergencyCard:    ModulePermission
  moduleFuneralWishes:    ModulePermission
  modulePersonalFarewell: ModulePermission
  moduleMedicalWishes:    ModulePermission
  moduleDocumentIndex:    ModulePermission
  moduleDigitalEstate:    ModulePermission
  moduleMessages:         ModulePermission
  moduleLifeStory:        ModulePermission
  setByUser:              boolean
  createdAt:              string
  updatedAt:              string
  syncStatus:             SyncStatus
}

export interface NextOfKinSession {
  id:                       string
  accessKeyId:              string
  userId:                   string
  sessionRole:              SessionRole
  createdAt:                string
  expiresAt:                string
  lastActivityAt:           string
  extendedAt:               string | null
  extendedUntil:            string | null
  deviceTokenHash:          string | null
  correctionWindowOpen:     boolean
  correctionWindowClosesAt: string | null
  status:                   SessionStatus
  revokedAt:                string | null
}

export interface AccessLogEntry {
  id:             string
  userId:         string
  actorRole:      ActorRole
  actorSessionId: string | null
  eventType:      LogEventType
  moduleAccessed: string | null
  targetRecordId: string | null
  occurredAt:     string
  ipRegion:       string | null
  deviceType:     DeviceType
  browserFamily:  string | null
  outcome:        LogOutcome
  failureReason:  string | null
  retainUntil:    string
  archivedAt:     string | null
  syncStatus:     SyncStatus
}

export interface FailedAttemptRecord {
  id:                string
  targetUserId:      string | null
  inputCodeHash:     string
  attemptedAt:       string
  ipRegion:          string | null
  deviceFingerprint: string | null
  attemptNumber:     number
  blockedUntil:      string | null
  blockReason:       'TOO_MANY_ATTEMPTS' | 'SUSPICIOUS_PATTERN' | null
  alertSentToOwner:  boolean
}

export interface MessageEnvelope {
  id:               string
  userId:           string
  createdAt:        string
  updatedAt:        string
  recipientLabel:   string | null
  encryptedContent: ArrayBuffer | null
  blobIv:           string | null
  contentPreview:   string | null
  draftStatus:      MessageDraftStatus
  releasedAt:       string | null
  firstOpenedAt:    string | null
  openedBySession:  string | null
  withdrawnAt:      string | null
  withdrawnReason:  string | null
  syncStatus:       SyncStatus
  cloudSyncId:      string | null
}

export interface InactivityProtocol {
  id:                  string
  userId:              string
  enabled:             boolean
  thresholdDays:       number
  lastOwnerLoginAt:    string
  activationTriggered: boolean
  triggeredAt:         string | null
  warningsSent:        number
  lastWarningSentAt:   string | null
}

export interface Wish {
  id:               string
  userId:           string
  category:         WishCategory
  sortOrder:        number
  completionStatus: CompletionStatus
  lastUpdatedAt:    string
  createdAt:        string
  syncStatus:       SyncStatus
  cloudSyncId:      string | null
  encryptedContent: ArrayBuffer | null
  blobIv:           string | null
}

export interface FuneralWishes {
  id:               string
  userId:           string
  completionStatus: CompletionStatus
  lastUpdatedAt:    string
  createdAt:        string
  syncStatus:       SyncStatus
  cloudSyncId:      string | null
  encryptedContent: ArrayBuffer | null
  blobIv:           string | null
}

export interface DecryptedFuneralWishes {
  ceremonyType:      string
  location:          string
  music:             string
  flowers:           string
  clothing:          string
  specialRequests:   string
  burialOrCremation: string
  funeralHome:       string
  notificationList:  string
  obituaryText:      string
}

export interface MedicalWishes {
  id:                         string
  userId:                     string
  completionStatus:           CompletionStatus
  hasLivingWill:              boolean
  hasResuscitationDirective:  boolean
  lastUpdatedAt:              string
  createdAt:                  string
  syncStatus:                 SyncStatus
  cloudSyncId:                string | null
  encryptedContent:           ArrayBuffer | null
  blobIv:                     string | null
}

export interface DecryptedMedicalWishes {
  treatmentPreferences:   string
  resuscitationWishes:    string
  organDonation:          string
  hospitalPreferences:    string
  gpName:                 string
  gpContact:              string
  livingWillLocation:     string
  proxyDecisionMaker:     string
  currentMedications:     string
}

export interface DocumentIndexEntry {
  id:               string
  userId:           string
  documentCategory: DocumentCategory
  documentLabel:    string
  sortOrder:        number
  isVerified:       boolean
  lastUpdatedAt:    string
  createdAt:        string
  syncStatus:       SyncStatus
  cloudSyncId:      string | null
  encryptedContent: ArrayBuffer | null
  blobIv:           string | null
}

export interface DigitalEstateEntry {
  id:               string
  userId:           string
  serviceCategory:  DigitalServiceCategory
  serviceLabel:     string
  desiredAction:    DigitalDesiredAction
  sortOrder:        number
  lastUpdatedAt:    string
  createdAt:        string
  syncStatus:       SyncStatus
  cloudSyncId:      string | null
  encryptedContent: ArrayBuffer | null
  blobIv:           string | null
}

export interface PersonalFarewell {
  id:               string
  userId:           string
  hasMainLetter:    boolean
  recipientCount:   number
  completionStatus: CompletionStatus
  lastUpdatedAt:    string
  createdAt:        string
  syncStatus:       SyncStatus
  cloudSyncId:      string | null
  encryptedContent: ArrayBuffer | null
  blobIv:           string | null
}

export interface AISignal {
  id:               string
  userId:           string
  moduleContext:    string
  signalType:       SignalType
  priority:         SignalPriority
  status:           SignalStatus
  generatedAt:      string
  readAt:           string | null
  dismissedAt:      string | null
  expiresAt:        string
  encryptedContent: ArrayBuffer | null
  blobIv:           string | null
}

export interface TimelineEvent {
  id:               string
  userId:           string
  eventCategory:    EventCategory
  eventLabel:       string
  occurredAt:       string
  actorType:        ActorType
  isReversible:     boolean
  relatedRecordId:  string | null
  relatedTable:     string | null
  syncStatus:       SyncStatus
  cloudSyncId:      string | null
  encryptedContent: ArrayBuffer | null
  blobIv:           string | null
}

export interface ModuleProgress {
  id:                   string
  userId:               string
  moduleName:           string
  completionPercentage: number
  stepsCurrent:         number
  stepsTotal:           number
  status:               CompletionStatus
  startedAt:            string | null
  completedAt:          string | null
  lastVisitedAt:        string
  syncStatus:           SyncStatus
  cloudSyncId:          string | null
}

export interface SyncQueueItem {
  id:            string
  tableName:     string
  recordId:      string
  operation:     'CREATE' | 'UPDATE' | 'DELETE'
  payload:       string
  queuedAt:      string
  attemptCount:  number
  lastAttemptAt: string | null
  status:        'PENDING' | 'IN_FLIGHT' | 'SYNCED' | 'FAILED'
  errorMessage:  string | null
  priority:      1 | 2 | 3
}

// ─── Service result types ──────────────────────────────────────────────────────

export interface ServiceError {
  code:     string
  message:  string
  details?: string
  severity: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL'
}

export interface Result<T> {
  succes:  boolean
  data:    T | null
  error:   ServiceError | null
}

// ─── UI types ──────────────────────────────────────────────────────────────────

export interface ToastItem {
  id:       string
  message:  string
  type:     'success' | 'error' | 'warning' | 'info'
  duration: number
}

export interface NavigationItem {
  label:    string
  path:     string
  icon:     string
  badge?:   number
  children?: NavigationItem[]
}

// ─── Onboarding types ─────────────────────────────────────────────────────────

export interface OnboardingData {
  // Stap 1: welkom (geen data)
  // Stap 2: naam
  firstName:    string
  lastName:     string
  // Stap 3: geboortedatum
  dateOfBirth:  string
  // Stap 4: contactgegevens
  email:        string
  phone:        string
  // Stap 5: woonplaats + volledig adres (V35-P2)
  street:              string
  houseNumber:         string
  houseNumberAddition: string
  city:                string
  postalCode:          string
  // Stap 6: burgerlijke staat
  maritalStatus: string
  // Stap 7: kinderen
  hasChildren:  boolean
  numberOfChildren: number
  // Stap 8: wachtwoord
  password:     string
  passwordConfirm: string
  // Stap 9: privacy bevestiging
  privacyAccepted:    boolean
  termsAccepted:      boolean
}
